import { FormikProvider, useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { Container, Form, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import contactProfiles from '../../api/contactProfiles/hooks'
import contacts from '../../api/contacts/hooks'
import { ContactType, HistoryState, PermissionsProps, RouteParams } from '../../api/globalTypes'
import AppPermissionsSwitcher from '../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../Components/AppPermissionsSwitcher/constants/actions'
import FormActionButtons from '../../Components/Common/FormActionButtons'
import Loader from '../../Components/Common/Loader'
import PageTitle from '../../Components/Common/PageTitle'
import ContactFormGeneralTab, {
    ContactFormGeneralTabMain,
    ContactFormGeneralTabSide,
} from '../../Components/ContactFormGeneralTab/ContactFormGeneralTab'
import ContactFormOtherInfoTab, {
    ContactFormOtherInfoTabMain,
    ContactFormOtherInfoTabSide,
} from '../../Components/ContactFormOtherInfoTab/ContactFormOtherInfoTab'
import ContactProfileFormTab from '../../Components/ContactProfileFormTab'
import { DocumentFormTab } from '../../Components/Documents'
import { useFormValidationErrorModal } from '../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import { useInitializeLocationFilters } from '../../hooks/useInitializeLocationFilters'
import { resetFormFilters } from '../../store/formFilters'
import { normalizeDecimalFields } from '../../utils/general'
import { contactFormTabs, fieldsToNormalize } from './constants'
import { useContactGeneralFormConfig, useContactOtherInfoFormConfig, useContactProfileFormConfig } from './hooks'
import { handleMultiFormSubmit } from './utils'

const ContactEdit = () => {
    const history = useHistory()
    const location = useLocation<HistoryState>()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { id } = useParams<RouteParams>()

    const tabs = contactFormTabs(true)

    // activeTab: contactProfile (ContactView action) ?? activeTab: general
    const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? tabs[0]?.name)
    const [profileId, setProfileId] = useState(location?.state?.profileId ?? '')

    const { data: contactData, isPending: getContactPending } = contacts.GetContact.useQuery(id)
    const { mutate: updateContact, isPending: updateContactPending } = contacts.CreateUpdateContact.useMutation()

    const contact = contactData?.item ?? null

    const { data: contactProfile, isLoading: getContactProfileLoading } = contactProfiles.GetContactProfile.useQuery(
        { contactId: id, profileId }
    )

    const { mutate: saveContactProfile, isPending: saveContactProfilePending } = contactProfiles.CreateUpdateContactProfile.useMutation()

    useInitializeLocationFilters(contactProfile)

    const { generalInputs, generalValidationConfig } = useContactGeneralFormConfig(contact, ContactType.Person)
    const { otherInfoInputs, otherInfoValidationConfig } = useContactOtherInfoFormConfig(contact, ContactType.Person)
    const { profileInputs, profileValidationConfig } = useContactProfileFormConfig(contactProfile, true)

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters({ context: 'location' }))
            dispatch(resetFormFilters({ context: 'contactProfile' }))
        }
    }, [dispatch])

    const handleTabChange = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const formikGeneral = useFormik({
        enableReinitialize: true,
        initialValues: {
            general: generalValidationConfig?.initialValues ?? {},
            otherInfo: otherInfoValidationConfig?.initialValues ?? {},
        },
        validationSchema: Yup.object({
            general: generalValidationConfig?.validationSchema ?? Yup.object({}),
            otherInfo: otherInfoValidationConfig?.validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values) => {
            const submitValues = {
                ...generalValidationConfig.prepareModelForSubmit(values.general),
                ...('prepareModelForSubmit' in otherInfoValidationConfig
                    ? otherInfoValidationConfig.prepareModelForSubmit(values.otherInfo)
                    : values.otherInfo),
            }

            updateContact({ data: submitValues, isEdit: true, id: +id, contactType: ContactType.Person })
        },
    })

    const formikProfile = useFormik({
        enableReinitialize: true,
        initialValues: {
            profile: {
                ...profileValidationConfig?.initialValues,
                ...normalizeDecimalFields(profileValidationConfig?.initialValues, fieldsToNormalize),
            },
        },
        validationSchema: Yup.object({
            profile: profileValidationConfig?.validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values) => {
            const submitValues = profileValidationConfig.prepareModelForSubmit(values.profile)

            if (profileId) {
                saveContactProfile({
                    data: { ...submitValues, contact_id: id },
                    contactId: id,
                    profileId: submitValues.id,
                    isEdit: true,
                })
                return
            }

            if (!submitValues?.property_type_id || !submitValues.contact_profile_service_id) return

            const { id: _id, ...createData } = submitValues

            saveContactProfile(
                { data: { ...createData, contact_id: id }, contactId: id, isEdit: false },
                { onSuccess: (data) => setProfileId(String(data.id)) }
            )
        },
    })

    useFormValidationErrorModal(formikGeneral, [generalInputs, otherInfoInputs, profileInputs])

    const renderFormButtons = () => (
        <FormActionButtons
            disabled={updateContactPending || saveContactProfilePending}
            hasCancel={true}
            cancelAction={() => history.push(`/contacts/${id}`)}
        />
    )

    document.title = `${t('edit')} ${contact?.contactPerson?.firstName ?? ''} ${contact?.contactPerson?.lastName ?? ''} | ${t('app.title')}`

    if (getContactPending || getContactProfileLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loader />
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content">
            <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                {({ hasModulePermission }: PermissionsProps) => hasModulePermission === false && <Redirect to="/contacts" />}
            </AppPermissionsSwitcher>
            <Container fluid>
                <FormikProvider value={formikGeneral}>
                    <Form onSubmit={async (e) => handleMultiFormSubmit(e, [formikGeneral, formikProfile])}>
                        <PageTitle title={`${t('page.edit')} ${contact?.contactPerson?.firstName} ${contact?.contactPerson?.lastName}`}>
                            {renderFormButtons()}
                        </PageTitle>

                        <TabContent activeTab={activeTab} className="text-muted">
                            {activeTab === 'general' && (
                                <TabPane tabId="general">
                                    <ContactFormGeneralTab
                                        subform="general"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        handleTabChange={handleTabChange}
                                        inputs={generalInputs}
                                        formik={formikGeneral}
                                        contactType={ContactType.Person}
                                    >
                                        <ContactFormGeneralTabSide />
                                        <ContactFormGeneralTabMain />
                                    </ContactFormGeneralTab>
                                </TabPane>
                            )}
                            {activeTab === 'otherInfo' && (
                                <TabPane tabId="otherInfo">
                                    <ContactFormOtherInfoTab
                                        subform="otherInfo"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        handleTabChange={handleTabChange}
                                        inputs={otherInfoInputs}
                                        formik={formikGeneral}
                                        contact={contact}
                                        contactType={ContactType.Person}
                                    >
                                        <ContactFormOtherInfoTabSide />
                                        <ContactFormOtherInfoTabMain />
                                    </ContactFormOtherInfoTab>
                                </TabPane>
                            )}
                            {activeTab === 'demand' && (
                                <TabPane tabId="demand">
                                    <ContactProfileFormTab
                                        subform="profile"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        handleTabChange={handleTabChange}
                                        inputs={profileInputs}
                                        formik={formikProfile}
                                        contactProfile={contactProfile}
                                        contactType={ContactType.Person}
                                    />
                                </TabPane>
                            )}
                        </TabContent>
                    </Form>
                </FormikProvider>

                {/* documents are saved via dropzone, not via the page's Formik instance */}
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <DocumentFormTab
                            tabs={tabs}
                            activeTab={activeTab}
                            handleTabChange={handleTabChange}
                            documentModule="contact"
                            contactType={ContactType.Person}
                        />
                    </TabPane>
                )}
            </Container>
        </div>
    )
}

export default ContactEdit
